import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import { isIOS } from 'lib/device'
import styles from './payload-container.module.scss'
import { MAX_VISIBLE_PAYLOAD_ITEMS } from './payloadImageLightbox'
import { RESERVED_AUDIO_CARD_HEIGHT } from 'lib/mediaBuckets'
import {
    combinedMediaAudioBadgePresentation,
    combinedMediaEnabled,
    combinedMediaPair,
    combinedMediaRow,
} from 'lib/combinedMedia'
import { MEDIA_PAYLOAD_PASSTHROUGH_ATTRIBUTE } from './mediaPayloadClickPropagation'
import { suppressNextClick } from 'lib/suppressNextClick'
import { getDisplayAiGenerationError, ImageGenerationFailureBubble } from './ImageRetryButton'
import {
    AudioLines,
    Check,
    ChevronLeft,
    ChevronRight,
    File,
    Film,
    GalleryHorizontal,
    Image,
    Info,
    LayoutGrid,
    ListMusic,
    MoreVertical,
    Music,
    PictureInPicture2,
    RefreshCw,
} from 'lucide-react'
import TvMinimalPlayIcon from 'components/shared/icons/TvMinimalPlayIcon'
import { formatAudioTime } from '../FileEmbed/AudioAttachmentPlayer'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import BsvTransactionStatusIcon from 'components/shared/BsvTransactionStatusIcon'
import { BSV_TX_STATES } from 'utils/bsvTransactionStatus'
import FileEmbed from '../FileEmbed'
import VideoPlayerCore from '../VideoPlayerCore'
import {
    openTrackInCinema,
    playTrackInDock,
    toggleTrackInPlaylist,
} from 'components/shared/MediaDock/mediaDockEvents'
import { PLAY_IN_DOCK_LABEL } from 'components/shared/MediaDock/mediaDockPlayInDock'
import {
    playlistMenuLabel,
    useMediaKeysInPlaylist,
} from 'components/shared/MediaDock/useMediaKeyInPlaylist'
import {
    blobIdFromPayloadUrl,
    payloadMediaStableIdentityKey,
    payloadUrlSearchParam,
    videoPayloadMediaKey,
} from './payloadMediaIdentity'
import {
    mediaBucketsEnabled,
    bucketedMediaHeight,
    payloadMediaGridLayout,
    resolveMediaFit,
    RESERVED_DIMLESS_MEDIA_HEIGHT,
    mobilePayloadMediaContentWidth,
    payloadMediaContentWidth,
    payloadMediaGridTileWidth,
    PAYLOAD_MEDIA_GRID_GAP_PX,
} from 'lib/mediaBuckets'
import { mediaDockCinemaEnabled } from 'lib/mediaDockCinema'
import { isActivePayloadStatus, isSingleActiveStatusPayload } from './payloadStatus'
import {
    combineMediaMenuGroups,
    type MediaMenuGroups,
    groupPayloadMenuActions,
    payloadChainActionLabel,
    shouldAddPayloadCinemaMenuAction,
    shouldAddPayloadPlaylistMenuAction,
    shouldShowPayloadTagMenuAction,
} from './payloadMediaMenu'
import { payloadMediaRowKind } from './payloadMediaKind'
import { isNativeIOSApp } from 'utils/nativeApp'
import { panelSwipeExclusionProps } from 'components/shared/panelSwipeExclusions'
import {
    defaultPayloadMediaLayout,
    payloadMediaCarouselCellBasis,
    payloadMediaCarouselHeight,
    PAYLOAD_MEDIA_CAROUSEL_GAP_PX,
    payloadMediaLayoutOverride,
    setPayloadMediaLayoutOverride,
    subscribePayloadMediaLayout,
    type PayloadMediaLayout,
} from 'lib/payloadMediaLayout'

type PayloadStatus =
    | 'pending'
    | 'running'
    | 'submitted'
    | 'succeeded'
    | 'failed'
    | 'canceled'
    | string

export type PayloadContainerItem = {
    id: string
    label?: string | null
    status?: PayloadStatus | null
    kind?: 'image' | 'video' | 'audio' | 'file' | 'run'
    // AI-run output, as opposed to media the author attached. Decides the default multi-item
    // layout (lib/payloadMediaLayout): attached media rides the carousel, generations the grid.
    generated?: boolean
    url?: string | null
    thumbnailUrl?: string | null
    previewUrls?: Record<string, string> | null
    message?: string | null
    errorMessage?: string | null
    render?: React.ReactNode
    controls?: React.ReactNode
    creatorFailurePreview?: React.ReactNode
    tagLabel?: string | null
    tagPreview?: React.ReactNode
    modelLabel?: string | null
    metadata?: string[] | null
    width?: number | null
    height?: number | null
    contentType?: string | null
    attachmentId?: string | null
    blobId?: string | null
    sourceAnswerId?: string | null
    sourceQuestId?: string | null
    mediaIdentityKey?: string | null
    refreshMediaUrl?: () => Promise<string | null | undefined>
    onTagClick?: React.MouseEventHandler<HTMLButtonElement>
    menuActions?: PayloadMenuAction[]
    chainIndicator?: PayloadChainIndicator | null
    // The ordinal minted from this attached file, if any (file_urls[].bsv_ordinal).
    bsvOrdinal?: { id: string; origin?: string | null } | null
    isMinting?: boolean
    mintingLabel?: string | null
    mintingFailed?: boolean
    mintedRecently?: boolean
    onClick?: React.MouseEventHandler
    onRetry?: React.MouseEventHandler<HTMLButtonElement>
    isRetrying?: boolean
}

type PayloadChainIndicator =
    | {
          kind: 'ordinal'
          ordinalId?: string
          questId?: string | null
          answerId?: string | null
      }
    | {
          kind: 'onchain'
          bsvTx?: Record<string, any> | null
          fallbackTxId?: string | null
      }

export type PayloadMenuAction = {
    label: string
    detail?: string | null
    href?: string | null
    download?: boolean
    downloadName?: string | null
    external?: boolean
    icon?: React.ReactNode
    chainIndicator?: PayloadChainIndicator | null
    disabled?: boolean
    working?: boolean
    keepOpenWhilePending?: boolean
    onMenuOpen?: () => void
    preview?: React.ReactNode
    onClick?: (
        event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
        context: { triggerElement: HTMLButtonElement | null },
    ) => void | Promise<unknown>
}

// Menu rows kept in their render buckets so a combined card can merge two media group by
// group instead of concatenating two flat lists.
type PayloadMenuGroups = MediaMenuGroups<PayloadMenuAction> & {
    kind: PayloadContainerItem['kind'] | null
}

type MediaLayoutSettledHandler = () => void

// 'auto' resolves the attached-vs-generated default plus the viewer's per-post override (keyed
// by mediaLayoutKey). An explicit layout pins it — surfaces without an answer behind them (the
// composer preview) stay on the grid.
export type PayloadMediaLayoutMode = 'auto' | PayloadMediaLayout

function usePayloadMediaLayoutOverride(
    key: string | null,
    onLayoutChange?: () => void,
): PayloadMediaLayout | null {
    const getSnapshot = useCallback(() => payloadMediaLayoutOverride(key), [key])
    const layout = useSyncExternalStore(subscribePayloadMediaLayout, getSnapshot, getSnapshot)
    const previous = useRef({ key, layout })
    useLayoutEffect(() => {
        const changed = previous.current.key === key && previous.current.layout !== layout
        previous.current = { key, layout }
        // Notify every mounted copy after its new DOM has committed, so measurement cannot
        // consume the shrink permission against the old grid before the store update.
        if (changed) onLayoutChange?.()
    }, [key, layout, onLayoutChange])
    return layout
}

// A carousel cell is framed visual media. Status chips, audio players and file embeds keep
// their own rows, so a row holding any of them stays a grid.
function isCarouselMediaItem(item: PayloadContainerItem): boolean {
    if (!item.url || item.render) return false

    const kind = mediaKindFromUrl(item)
    return kind === 'image' || kind === 'video'
}

function statusLabel(status?: PayloadStatus | null): string {
    switch (status) {
        case 'pending':
            return 'Pending'
        case 'running':
        case 'started':
            return 'Running'
        case 'submitted':
            return 'Submitted'
        case 'succeeded':
        case 'finished':
            return 'Done'
        case 'failed':
            return 'Failed'
        case 'canceled':
            return 'Canceled'
        default:
            return status ? String(status) : 'Pending'
    }
}

function displayText(value: unknown): string | null {
    if (value == null) return null
    if (typeof value === 'string') {
        const trimmed = value.trim()
        return trimmed.length ? trimmed : null
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    return null
}

export function isCompleteStatus(status?: PayloadStatus | null): boolean {
    return status === 'succeeded' || status === 'finished'
}

function quotedHeaderValue(value: string): string {
    return value.trim().replace(/^"(.*)"$/, '$1')
}

function filenameFromContentDisposition(value: string | null): string | null {
    if (!value) return null

    const encodedMatch = value.match(/filename\*=UTF-8''([^;]+)/i)
    if (encodedMatch?.[1]) {
        try {
            return decodeURIComponent(quotedHeaderValue(encodedMatch[1]))
        } catch {
            return quotedHeaderValue(encodedMatch[1])
        }
    }

    const filenameMatch = value.match(/filename=([^;]+)/i)
    return filenameMatch?.[1] ? quotedHeaderValue(filenameMatch[1]) : null
}

function triggerBlobDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.style.display = 'none'
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
}

function triggerDirectDownload(href: string, filename: string): void {
    const link = document.createElement('a')
    link.style.display = 'none'
    link.href = href
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

async function fetchAndDownloadPayloadMedia(action: PayloadMenuAction): Promise<void> {
    if (!action.href) return

    const response = await fetch(action.href)
    if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`)
    }

    const blob = await response.blob()
    const filename =
        filenameFromContentDisposition(response.headers.get('content-disposition')) ||
        action.downloadName ||
        action.label ||
        'media'
    triggerBlobDownload(blob, filename)
}

function isStatusOnlyItem(item: PayloadContainerItem): boolean {
    return !item.url && !item.render && !isCompleteStatus(item.status)
}

export function mediaKindFromUrl(item: PayloadContainerItem): PayloadContainerItem['kind'] {
    const url = item.url || ''
    const label = item.label || item.modelLabel || ''
    const contentType = String(item.contentType || '').toLowerCase()
    const declaredKind = item.kind
    const isKnownKind =
        declaredKind === 'image' ||
        declaredKind === 'video' ||
        declaredKind === 'audio' ||
        declaredKind === 'file'
    // Audio extensions win even over a mislabeled kind: .m4a files are mp4
    // containers and often arrive marked as video.
    if (/\.(mp3|wav|ogg|oga|opus|flac|m4a|aac)(\?|$)/i.test(url)) return 'audio'
    if (/\.(mp3|wav|ogg|oga|opus|flac|m4a|aac)(\?|$)/i.test(label)) return 'audio'

    if (contentType.startsWith('image/')) return 'image'
    if (contentType.startsWith('video/')) return 'video'
    if (contentType.startsWith('audio/')) return 'audio'
    if (contentType.startsWith('model/') || contentType.includes('gltf')) return 'file'
    if (/\.(obj|glb|gltf|stl)(\?|$)/i.test(url) || /\.(obj|glb|gltf|stl)(\?|$)/i.test(label)) {
        return 'file'
    }

    if (isKnownKind) return declaredKind

    if (/\.(png|jpe?g|webp|gif|avif)(\?|$)/i.test(url)) return 'image'
    if (/\.(mp4|mov|webm|m4v)(\?|$)/i.test(url)) return 'video'
    return 'file'
}

const PAYLOAD_MEDIA_RETRY_LIMIT = 3
const PAYLOAD_IMAGE_LOAD_CACHE_LIMIT = 300
const DEFAULT_PAYLOAD_VIDEO_ASPECT_RATIO_STYLE: React.CSSProperties = { aspectRatio: '1 / 1' }
const loadedPayloadImageUrlsByKey = new Map<string, string>()

function appendCacheBustParam(url: string, retryCount: number): string {
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}break_cache=true&retry=${retryCount}`
}

function appendUrlParamIfMissing(
    url: string | null | undefined,
    key: string,
    value: string | null | undefined,
): string | null | undefined {
    if (!url || !value || payloadUrlSearchParam(url, key)) return url

    const [baseWithQuery, fragment] = url.split('#', 2)
    const separator = baseWithQuery.includes('?') ? '&' : '?'
    const nextUrl = `${baseWithQuery}${separator}${encodeURIComponent(key)}=${encodeURIComponent(
        value,
    )}`
    return fragment ? `${nextUrl}#${fragment}` : nextUrl
}

function currentApiBaseUrl(): string | null {
    if (typeof window === 'undefined') return null

    const rawApiUrl = (window as any).knovApiUrl
    if (typeof rawApiUrl !== 'string' || rawApiUrl.trim() === '') return null

    return rawApiUrl.replace(/\/+$/, '')
}

function payloadVideoThumbnailUrl(item: PayloadContainerItem): string | null | undefined {
    const thumbnailUrl = item.thumbnailUrl || payloadVideoStablePreviewUrl(item)
    if (!thumbnailUrl) return thumbnailUrl
    if (!payloadAnswerMediaUrl(thumbnailUrl)) return thumbnailUrl

    return appendUrlParamIfMissing(
        thumbnailUrl,
        'vtoken',
        payloadUrlSearchParam(item.url, 'vtoken'),
    )
}

function payloadVideoStablePreviewUrl(item: PayloadContainerItem): string | null {
    const answerId = item.sourceAnswerId || payloadAnswerIdFromMediaUrl(item.url)
    const attachmentId = item.attachmentId || payloadUrlSearchParam(item.url, 'attachment_id')
    const apiBaseUrl = currentApiBaseUrl()
    if (!answerId || !attachmentId || !apiBaseUrl) return null

    const params = new URLSearchParams()
    params.set('attachment_id', attachmentId)
    params.set('size', 'preview')
    params.set('preview_key', 'video')
    return `${apiBaseUrl}/api/v1/answers/${encodeURIComponent(answerId)}/media?${params.toString()}`
}

function payloadAnswerMediaUrl(url: string | null | undefined): boolean {
    return Boolean(url && /\/api\/v1\/answers\/[^/?#]+\/media(?:[?#]|$)/.test(url))
}

function payloadAnswerIdFromMediaUrl(url: string | null | undefined): string | null {
    if (!url) return null

    const match = url.match(/\/api\/v1\/answers\/([^/?#]+)\/media/)
    return match?.[1] ? decodeURIComponent(match[1]) : null
}

function cacheBustedPayloadMediaUrl(
    url: string | null | undefined,
    item: PayloadContainerItem,
    retryCount: number,
    useItemBlobFallback = false,
): string | null | undefined {
    if (!url || retryCount <= 0) return url

    const urlBlobId = blobIdFromPayloadUrl(url)
    const blobId = urlBlobId || (useItemBlobFallback ? item.blobId : null)
    const apiBaseUrl = currentApiBaseUrl()
    if (blobId && apiBaseUrl) {
        const params = new URLSearchParams()
        if (urlBlobId) {
            const query = url.split('?')[1]?.split('#')[0]
            if (query) {
                new URLSearchParams(query).forEach((value, key) => {
                    params.set(key, value)
                })
            }
        }
        params.set('break_cache', 'true')
        params.set('retry', String(retryCount))
        const queryString = params.toString()
        return `${apiBaseUrl}/api/v1/blob/${encodeURIComponent(blobId)}${
            queryString ? `?${queryString}` : ''
        }`
    }

    return appendCacheBustParam(url, retryCount)
}

async function refreshPayloadMediaUrl(item: PayloadContainerItem): Promise<string | null> {
    if (!item.refreshMediaUrl) return null

    try {
        const refreshedUrl = await item.refreshMediaUrl()
        return typeof refreshedUrl === 'string' && refreshedUrl.trim() ? refreshedUrl : null
    } catch {
        return null
    }
}

function numericImageDimension(value: unknown): number | null {
    const dimension = Number(value)
    return Number.isFinite(dimension) && dimension > 0 ? Math.round(dimension) : null
}

function mediaAspectRatioStyle(item: PayloadContainerItem): React.CSSProperties | undefined {
    const width = numericImageDimension(item.width)
    const height = numericImageDimension(item.height)
    if (!width || !height) return undefined

    return { aspectRatio: `${width} / ${height}` }
}

function payloadMediaIdentityKey(item: PayloadContainerItem): string | null {
    return payloadMediaStableIdentityKey(item) || item.url || null
}

function payloadMediaDockKey(
    item: PayloadContainerItem,
    kind = mediaKindFromUrl(item),
): string | null {
    if (kind === 'audio') {
        const identityKey = payloadMediaIdentityKey(item) || item.attachmentId || item.id
        return identityKey ? `audio-attachment:${identityKey}` : null
    }

    if (kind === 'video') {
        const identityKey = payloadMediaIdentityKey(item)
        return identityKey ? `payload-video:${identityKey}` : null
    }

    return null
}

function rememberLoadedPayloadImage(cacheKey: string | null, imageUrl: string | null | undefined) {
    if (!cacheKey || !imageUrl) return

    if (loadedPayloadImageUrlsByKey.has(cacheKey)) {
        loadedPayloadImageUrlsByKey.delete(cacheKey)
    }
    loadedPayloadImageUrlsByKey.set(cacheKey, imageUrl)

    while (loadedPayloadImageUrlsByKey.size > PAYLOAD_IMAGE_LOAD_CACHE_LIMIT) {
        const oldestKey = loadedPayloadImageUrlsByKey.keys().next().value
        if (!oldestKey) break
        loadedPayloadImageUrlsByKey.delete(oldestKey)
    }
}

function forgetLoadedPayloadImage(cacheKey: string | null) {
    if (!cacheKey) return
    loadedPayloadImageUrlsByKey.delete(cacheKey)
}

function rememberedLoadedPayloadImageUrl(cacheKey: string | null): string | null {
    if (!cacheKey) return null
    return loadedPayloadImageUrlsByKey.get(cacheKey) || null
}

function useMediaLayoutSettled(
    onMediaLayoutSettled: MediaLayoutSettledHandler | undefined,
    settledKey: string | null,
) {
    useLayoutEffect(() => {
        if (!onMediaLayoutSettled || !settledKey) return

        if (typeof window.requestAnimationFrame !== 'function') {
            onMediaLayoutSettled()
            return
        }

        const frameId = window.requestAnimationFrame(onMediaLayoutSettled)
        return () => window.cancelAnimationFrame(frameId)
    }, [onMediaLayoutSettled, settledKey])
}

function payloadItemsEqual(
    prevItem: PayloadContainerItem,
    nextItem: PayloadContainerItem,
): boolean {
    const prevStableIdentity = payloadMediaStableIdentityKey(prevItem)
    const nextStableIdentity = payloadMediaStableIdentityKey(nextItem)
    const prevKind = mediaKindFromUrl(prevItem)
    const nextKind = mediaKindFromUrl(nextItem)

    if (prevKind === 'audio' && (prevStableIdentity || nextStableIdentity)) {
        return prevStableIdentity === nextStableIdentity && prevKind === nextKind
    }

    if (prevKind === 'video' && (prevStableIdentity || nextStableIdentity)) {
        return (
            prevStableIdentity === nextStableIdentity &&
            prevKind === nextKind &&
            prevItem.url === nextItem.url &&
            prevItem.thumbnailUrl === nextItem.thumbnailUrl &&
            prevItem.label === nextItem.label &&
            prevItem.width === nextItem.width &&
            prevItem.height === nextItem.height &&
            prevItem.render === nextItem.render
        )
    }

    const shouldCompareUrl = !prevStableIdentity && !nextStableIdentity

    return (
        prevItem.id === nextItem.id &&
        (!shouldCompareUrl || prevItem.url === nextItem.url) &&
        prevItem.thumbnailUrl === nextItem.thumbnailUrl &&
        prevItem.label === nextItem.label &&
        prevItem.contentType === nextItem.contentType &&
        prevItem.attachmentId === nextItem.attachmentId &&
        prevItem.blobId === nextItem.blobId &&
        prevItem.sourceAnswerId === nextItem.sourceAnswerId &&
        prevItem.mediaIdentityKey === nextItem.mediaIdentityKey &&
        prevItem.width === nextItem.width &&
        prevItem.height === nextItem.height &&
        prevItem.render === nextItem.render &&
        payloadMediaIdentityKey(prevItem) === payloadMediaIdentityKey(nextItem)
    )
}

const PayloadVideo = React.memo(
    function PayloadVideo({
        item,
        onMediaLayoutSettled,
        availableTileWidth = null,
        frameHeightOverride = null,
        mediaFit = null,
        topRightControls = null,
    }: {
        item: PayloadContainerItem
        onMediaLayoutSettled?: MediaLayoutSettledHandler
        availableTileWidth?: number | null
        frameHeightOverride?: number | null
        mediaFit?: 'contain' | 'cover' | null
        topRightControls?: React.ReactNode
    }) {
        const mediaKey = useMemo(
            () => videoPayloadMediaKey(item),
            [item.attachmentId, item.blobId, item.id, item.mediaIdentityKey, item.url],
        )
        const [mediaRetryCount, setMediaRetryCount] = useState(0)
        const [refreshedMediaUrl, setRefreshedMediaUrl] = useState<string | null>(null)
        const [measuredDimensions, setMeasuredDimensions] = useState<{
            width: number
            height: number
        } | null>(null)

        useEffect(() => {
            setMediaRetryCount(0)
            setRefreshedMediaUrl(null)
            setMeasuredDimensions(null)
        }, [mediaKey])

        const thumbnailUrl = payloadVideoThumbnailUrl(item)
        const width = numericImageDimension(item.width) || measuredDimensions?.width || null
        const height = numericImageDimension(item.height) || measuredDimensions?.height || null
        const aspectRatioStyle =
            mediaAspectRatioStyle(item) ||
            (width && height ? { aspectRatio: `${width} / ${height}` } : undefined) ||
            DEFAULT_PAYLOAD_VIDEO_ASPECT_RATIO_STYLE
        // Fixed frame from payload dims only. Without server/payload dims, reserve
        // the square fallback bucket that the estimators also assume; client-side media loads
        // must not choose a new bucket on a later virtual remount. On mobile the frame is
        // clamped to the width the tile actually renders at (shared width contract in
        // lib/mediaBuckets) so contain-fit media never reserves letterbox bands.
        const payloadWidth = numericImageDimension(item.width)
        const payloadHeight = numericImageDimension(item.height)
        const ownBucketHeight = useMemo(() => {
            if (!mediaBucketsEnabled()) return null
            return payloadWidth && payloadHeight
                ? bucketedMediaHeight(payloadWidth, payloadHeight, availableTileWidth)
                : RESERVED_DIMLESS_MEDIA_HEIGHT
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [mediaKey, payloadWidth, payloadHeight, availableTileWidth])
        // 3-4 tile grids replace per-tile buckets with the group's uniform row height (see
        // MediaContainer) so stacked rows never exceed the one-media-unit frame.
        const bucketHeight = ownBucketHeight == null ? null : frameHeightOverride ?? ownBucketHeight
        const frameStyle: React.CSSProperties | undefined = bucketHeight
            ? { height: `${bucketHeight}px`, width: '100%' }
            : aspectRatioStyle
        const mediaUrl =
            refreshedMediaUrl || cacheBustedPayloadMediaUrl(item.url, item, mediaRetryCount)
        const settledAspectRatioKey = bucketHeight
            ? `video:${mediaKey || item.id}:bucket:${bucketHeight}`
            : width && height
            ? `video:${mediaKey || item.id}:${width}:${height}`
            : null
        useMediaLayoutSettled(onMediaLayoutSettled, settledAspectRatioKey)

        const handleLoadedMetadata = useCallback(
            (event: React.SyntheticEvent<HTMLVideoElement>) => {
                const nextWidth = numericImageDimension(event.currentTarget.videoWidth)
                const nextHeight = numericImageDimension(event.currentTarget.videoHeight)
                if (nextWidth && nextHeight) {
                    setMeasuredDimensions({ width: nextWidth, height: nextHeight })
                }
            },
            [],
        )

        const handlePosterLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
            const nextWidth = numericImageDimension(event.currentTarget.naturalWidth)
            const nextHeight = numericImageDimension(event.currentTarget.naturalHeight)
            if (nextWidth && nextHeight) {
                setMeasuredDimensions({ width: nextWidth, height: nextHeight })
            }
        }, [])

        const handleResolveVideoRetrySrc = useCallback(
            async ({
                retryCount,
                currentSrc,
            }: {
                retryCount: number
                currentSrc: string | null
            }) => {
                if (refreshedMediaUrl) {
                    setRefreshedMediaUrl(null)
                    setMediaRetryCount(retryCount)
                    return cacheBustedPayloadMediaUrl(item.url, item, retryCount)
                }

                const nextUrl = await refreshPayloadMediaUrl(item)
                if (nextUrl && nextUrl !== currentSrc) {
                    setMediaRetryCount(0)
                    setRefreshedMediaUrl(nextUrl)
                    return nextUrl
                }

                setMediaRetryCount(retryCount)
                return cacheBustedPayloadMediaUrl(item.url, item, retryCount)
            },
            [item, refreshedMediaUrl],
        )

        return (
            <div className={styles.payloadVideoFrame} style={frameStyle}>
                <VideoPlayerCore
                    mediaKey={mediaKey}
                    src={mediaUrl}
                    type={item.contentType || undefined}
                    trackAnswerId={
                        item.sourceAnswerId || payloadAnswerIdFromMediaUrl(item.url) || undefined
                    }
                    trackQuestId={item.sourceQuestId || undefined}
                    posterSrc={thumbnailUrl}
                    width={width}
                    height={height}
                    containerClassName={styles.payloadVideoCoreContainer}
                    // A cover-fit cell fills its frame: an explicit box replaces the player's
                    // own aspect-ratio container, which would letterbox inside the frame.
                    containerStyle={
                        mediaFit === 'cover' ? { width: '100%', height: '100%' } : undefined
                    }
                    posterClassName={styles.payloadVideoPoster}
                    videoClassName={styles.payloadVideo}
                    preloadVideoLink
                    preloadVideoOnHover
                    fallbackAspectRatio="1 / 1"
                    loadWithoutPoster={false}
                    onLoadedMetadata={handleLoadedMetadata}
                    onPosterLoad={handlePosterLoad}
                    onResolveVideoRetrySrc={handleResolveVideoRetrySrc}
                    topRightControls={topRightControls}
                />
            </div>
        )
    },
    (prevProps, nextProps) =>
        payloadItemsEqual(prevProps.item, nextProps.item) &&
        prevProps.onMediaLayoutSettled === nextProps.onMediaLayoutSettled &&
        prevProps.availableTileWidth === nextProps.availableTileWidth &&
        prevProps.frameHeightOverride === nextProps.frameHeightOverride &&
        prevProps.mediaFit === nextProps.mediaFit &&
        prevProps.topRightControls === nextProps.topRightControls,
)

function PayloadMedia({
    item,
    onMediaLayoutSettled,
    availableTileWidth = null,
    frameHeightOverride = null,
    mediaFit = null,
    topRightControls = null,
    hugImageWidth = false,
}: {
    item: PayloadContainerItem
    onMediaLayoutSettled?: MediaLayoutSettledHandler
    availableTileWidth?: number | null
    frameHeightOverride?: number | null
    mediaFit?: 'contain' | 'cover' | null
    topRightControls?: React.ReactNode
    hugImageWidth?: boolean
}) {
    if (item.render) return <>{item.render}</>

    const kind = mediaKindFromUrl(item)

    if (kind === 'image' && item.url) {
        return (
            <PayloadImage
                item={item}
                onMediaLayoutSettled={onMediaLayoutSettled}
                availableTileWidth={availableTileWidth}
                frameHeightOverride={frameHeightOverride}
                mediaFit={mediaFit}
                hugWidth={hugImageWidth}
            />
        )
    }

    if (kind === 'video' && item.url) {
        return (
            <PayloadVideo
                item={item}
                onMediaLayoutSettled={onMediaLayoutSettled}
                availableTileWidth={availableTileWidth}
                frameHeightOverride={frameHeightOverride}
                mediaFit={mediaFit}
                topRightControls={topRightControls}
            />
        )
    }

    if (kind === 'audio' && item.url) {
        return <PayloadAudio item={item} />
    }

    if (kind === 'file' && item.url) {
        const previewUrl = item.thumbnailUrl || item.previewUrls?.['1x1'] || null
        const file = {
            id: item.attachmentId || item.id,
            attachment_id: item.attachmentId || undefined,
            blob_id: item.blobId || undefined,
            url: item.url,
            name: item.label || 'File',
            filename: item.label || 'File',
            content_type: item.contentType || undefined,
            type: item.contentType || undefined,
            preview_url: previewUrl || undefined,
            preview_urls: item.previewUrls || (previewUrl ? { '1x1': previewUrl } : undefined),
            bsv_ordinal: item.bsvOrdinal || undefined,
        }

        return (
            <div className={styles.payloadFile}>
                <FileEmbed
                    answerId={item.sourceAnswerId || undefined}
                    file={file}
                    showDelete={false}
                    canDelete={false}
                    showBsvStatus={false}
                />
            </div>
        )
    }

    return (
        <div className={styles.payloadFallback}>
            <i className="fa fa-file-o" />
        </div>
    )
}

const PayloadAudio = React.memo(
    function PayloadAudio({
        item,
        overlay = false,
    }: {
        item: PayloadContainerItem
        overlay?: boolean
    }) {
        const [retryCount, setRetryCount] = useState(0)
        const [refreshedUrl, setRefreshedUrl] = useState<string | null>(null)
        const audioUrl = refreshedUrl || cacheBustedPayloadMediaUrl(item.url, item, retryCount)
        const audioIdentityKey = payloadMediaIdentityKey(item) || item.attachmentId || item.id

        // Overlay-only badge state, fed by the player's existing callbacks. The elapsed label
        // is stored formatted so timeupdate ticks only re-render once per displayed second.
        const [audioPlaying, setAudioPlaying] = useState(false)
        const [audioDockOwned, setAudioDockOwned] = useState(false)
        const [audioDurationLabel, setAudioDurationLabel] = useState<string | null>(null)
        const [audioElapsedLabel, setAudioElapsedLabel] = useState<string | null>(null)

        const handleAudioDurationChange = useCallback((duration: number) => {
            setAudioDurationLabel(duration > 0 ? formatAudioTime(duration) : null)
        }, [])

        const handleAudioProgressChange = useCallback((currentTime: number) => {
            const nextLabel = formatAudioTime(currentTime)
            setAudioElapsedLabel(previous => (previous === nextLabel ? previous : nextLabel))
        }, [])

        useEffect(() => {
            setRetryCount(0)
            setRefreshedUrl(null)
        }, [item.url])

        useEffect(() => {
            setAudioPlaying(false)
            setAudioDockOwned(false)
            setAudioDurationLabel(null)
            setAudioElapsedLabel(null)
        }, [audioIdentityKey])

        const retryAudioMedia = useCallback(() => {
            setRetryCount(count => (count < PAYLOAD_MEDIA_RETRY_LIMIT ? count + 1 : count))
        }, [])

        const handleAudioError = useCallback(async () => {
            if (refreshedUrl) {
                setRefreshedUrl(null)
                retryAudioMedia()
                return
            }

            const nextUrl = await refreshPayloadMediaUrl(item)
            if (nextUrl && nextUrl !== audioUrl) {
                setRetryCount(0)
                setRefreshedUrl(nextUrl)
                return
            }

            retryAudioMedia()
        }, [audioUrl, item, refreshedUrl, retryAudioMedia])

        const audioFile = {
            id: item.attachmentId || item.id,
            url: audioUrl || item.url,
            name: item.label || item.modelLabel || 'Audio',
            filename: item.label || item.modelLabel || 'Audio',
            content_type: item.contentType || 'audio/mpeg',
            preview_url: item.thumbnailUrl || item.previewUrls?.['1x1'] || undefined,
        }

        const audioLayoutStyle = {
            '--payload-audio-min-height': `${RESERVED_AUDIO_CARD_HEIGHT}px`,
        } as React.CSSProperties

        // Overlaid on the artwork: the transport claims no vertical band of its own, so it
        // and drops the reserved-surface floor along with the compact two-row grid.
        if (overlay) {
            const { live: audioBadgeLive, label: audioBadgeLabel } =
                combinedMediaAudioBadgePresentation({
                    isPlaying: audioPlaying,
                    isDockOwned: audioDockOwned,
                    durationLabel: audioDurationLabel,
                    elapsedLabel: audioElapsedLabel,
                })
            return (
                <>
                    <div className={styles.payloadCombinedMediaBadge} aria-hidden="true">
                        {audioBadgeLive ? (
                            <span className={styles.payloadCombinedMediaBadgeEq}>
                                <span />
                                <span />
                                <span />
                            </span>
                        ) : (
                            <Music className={styles.payloadCombinedMediaBadgeIcon} />
                        )}
                        {audioBadgeLabel && <span>{audioBadgeLabel}</span>}
                    </div>
                    {/* The strip lies on artwork the answer owns. Its controls each stop their
                        own event, so marking the region and releasing the player's root click
                        lets a click on the inert gaps between them reach the answer's openRight
                        handler instead of dying here — see mediaPayloadClickPropagation. */}
                    <div
                        className={styles.payloadCombinedMediaTransport}
                        {...{ [MEDIA_PAYLOAD_PASSTHROUGH_ATTRIBUTE]: 'true' }}
                    >
                        <FileEmbed
                            answerId={item.sourceAnswerId || undefined}
                            file={audioFile}
                            showDelete={false}
                            canDelete={false}
                            audioPersistPlayback
                            audioTheme="scrim"
                            audioVolumeFlyout
                            audioBackgroundClickPassthrough
                            audioPlayerClassName={styles.payloadCombinedMediaEmbed}
                            audioPlaybackKey={audioIdentityKey || undefined}
                            audioOnPlaybackError={handleAudioError}
                            audioOnDurationChange={handleAudioDurationChange}
                            audioOnProgressChange={handleAudioProgressChange}
                            audioOnPlaybackStateChange={setAudioPlaying}
                            audioOnDockOwnedChange={setAudioDockOwned}
                            showBsvStatus={false}
                        />
                    </div>
                </>
            )
        }

        return (
            <div className={styles.payloadAudio} style={audioLayoutStyle}>
                <FileEmbed
                    answerId={item.sourceAnswerId || undefined}
                    file={audioFile}
                    showDelete={false}
                    canDelete={false}
                    audioPersistPlayback
                    audioPlayerCompact
                    audioPlayerClassName={styles.payloadAudioEmbed}
                    audioPlaybackKey={audioIdentityKey || undefined}
                    audioOnPlaybackError={handleAudioError}
                    showBsvStatus={false}
                />
            </div>
        )
    },
    (prevProps, nextProps) =>
        prevProps.overlay === nextProps.overlay &&
        payloadItemsEqual(prevProps.item, nextProps.item),
)

const PayloadImage = React.memo(
    function PayloadImage({
        item,
        onMediaLayoutSettled,
        availableTileWidth = null,
        frameHeightOverride = null,
        mediaFit = null,
        hugWidth = false,
    }: {
        item: PayloadContainerItem
        onMediaLayoutSettled?: MediaLayoutSettledHandler
        availableTileWidth?: number | null
        frameHeightOverride?: number | null
        mediaFit?: 'contain' | 'cover' | null
        hugWidth?: boolean
    }) {
        const imageRef = useRef<HTMLImageElement | null>(null)
        const payloadWidth = numericImageDimension(item.width)
        const payloadHeight = numericImageDimension(item.height)
        const aspectRatioStyle = mediaAspectRatioStyle(item)
        const stableImageKey = useMemo(
            () => payloadMediaStableIdentityKey(item),
            [item.attachmentId, item.blobId, item.mediaIdentityKey, item.url],
        )
        // Quantize each image to a fixed height per aspect class so the virtualized
        // stream never measures/re-layouts media. Geometry depends only on server/payload dims
        // plus the shared mobile width contract (lib/mediaBuckets), which clamps the frame to
        // the width the tile renders at so contain-fit never reserves letterbox bands; without
        // any dims the frame pins to the square fallback for every virtual remount.
        // Images remain contained so geometry locking never crops inspectable content.
        const useMediaBuckets = mediaBucketsEnabled()
        const {
            width,
            height,
            bucketHeight: ownBucketHeight,
        } = useMemo(() => {
            const resolvedWidth = payloadWidth || null
            const resolvedHeight = payloadHeight || null
            return {
                width: resolvedWidth,
                height: resolvedHeight,
                bucketHeight: useMediaBuckets
                    ? resolvedWidth && resolvedHeight
                        ? bucketedMediaHeight(resolvedWidth, resolvedHeight, availableTileWidth)
                        : RESERVED_DIMLESS_MEDIA_HEIGHT
                    : null,
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [stableImageKey, payloadWidth, payloadHeight, useMediaBuckets, availableTileWidth])
        // 3-4 tile grids replace per-tile buckets with the group's uniform row height (see
        // MediaContainer) so stacked rows never exceed the one-media-unit frame.
        const bucketHeight = ownBucketHeight == null ? null : frameHeightOverride ?? ownBucketHeight
        const bucketFit =
            bucketHeight && width && height ? resolveMediaFit(width, height, bucketHeight) : null
        // A lone image sits on the post's left edge with its frame shrunk to the contained
        // image, instead of centering inside a full-width frame with empty side bands. Only the
        // width changes, so reserved row heights stay bucket-exact.
        const huggedFrameWidth =
            hugWidth && bucketHeight && width && height
                ? Math.max(1, Math.round((bucketHeight * width) / height))
                : null
        const frameStyle: React.CSSProperties | undefined = bucketHeight
            ? huggedFrameWidth
                ? { height: `${bucketHeight}px`, width: `${huggedFrameWidth}px`, maxWidth: '100%' }
                : { height: `${bucketHeight}px`, width: '100%' }
            : aspectRatioStyle
        const imageStyle: React.CSSProperties | undefined = bucketHeight
            ? { width: '100%', height: '100%', objectFit: mediaFit ?? bucketFit ?? 'contain' }
            : mediaFit
            ? { ...aspectRatioStyle, objectFit: mediaFit }
            : aspectRatioStyle
        const resetKey = stableImageKey || item.url || null
        const [retryCount, setRetryCount] = useState(0)
        const [refreshedUrl, setRefreshedUrl] = useState<string | null>(null)
        const imageUrl = refreshedUrl || cacheBustedPayloadMediaUrl(item.url, item, retryCount)
        const imageCacheKey = stableImageKey || imageUrl || null
        const [displayUrl, setDisplayUrl] = useState<string | null>(
            () => (stableImageKey ? rememberedLoadedPayloadImageUrl(stableImageKey) : null) || null,
        )
        const visibleImageUrl = displayUrl || imageUrl
        const [loaded, setLoaded] = useState(() =>
            Boolean(rememberedLoadedPayloadImageUrl(imageCacheKey)),
        )
        const [failed, setFailed] = useState(false)
        const settledImageKey = loaded
            ? `image:${imageCacheKey || visibleImageUrl || item.id}:${width || 'auto'}:${
                  height || 'auto'
              }:${bucketHeight ?? 'nat'}`
            : null
        useMediaLayoutSettled(onMediaLayoutSettled, settledImageKey)

        const markLoaded = useCallback(() => {
            rememberLoadedPayloadImage(imageCacheKey, visibleImageUrl)
            setFailed(false)
            setLoaded(true)
        }, [imageCacheKey, visibleImageUrl])

        useLayoutEffect(() => {
            const rememberedUrl = stableImageKey
                ? rememberedLoadedPayloadImageUrl(stableImageKey)
                : null
            setDisplayUrl(rememberedUrl || imageUrl || null)
            setLoaded(
                Boolean(
                    rememberedUrl ||
                        rememberedLoadedPayloadImageUrl(stableImageKey || imageUrl || null),
                ),
            )
            setFailed(false)
            setRetryCount(0)
            setRefreshedUrl(null)
        }, [resetKey])

        useEffect(() => {
            if (!imageUrl) return

            if (!stableImageKey || !displayUrl || displayUrl === imageUrl) {
                if (!displayUrl) setDisplayUrl(imageUrl)
                return
            }

            let cancelled = false
            const nextImage = new window.Image()
            nextImage.onload = () => {
                if (cancelled) return
                rememberLoadedPayloadImage(stableImageKey, imageUrl)
                setDisplayUrl(imageUrl)
                setFailed(false)
                setLoaded(true)
            }
            nextImage.onerror = () => {
                if (cancelled) return
                forgetLoadedPayloadImage(stableImageKey)
            }
            nextImage.src = imageUrl

            return () => {
                cancelled = true
            }
        }, [displayUrl, imageUrl, stableImageKey])

        useLayoutEffect(() => {
            const image = imageRef.current
            if (image?.complete && image.naturalWidth > 0) {
                markLoaded()
            }
        }, [markLoaded])

        const retryImageMedia = useCallback(() => {
            setRetryCount(count => {
                if (count < PAYLOAD_MEDIA_RETRY_LIMIT) return count + 1

                setFailed(true)
                return count
            })
        }, [])

        const handleImageError = useCallback(async () => {
            if (displayUrl && imageUrl && displayUrl !== imageUrl) {
                forgetLoadedPayloadImage(stableImageKey)
                setDisplayUrl(imageUrl)
                setLoaded(false)
                return
            }

            forgetLoadedPayloadImage(imageCacheKey)

            if (refreshedUrl) {
                setRefreshedUrl(null)
                retryImageMedia()
                return
            }

            const nextUrl = await refreshPayloadMediaUrl(item)
            if (nextUrl && nextUrl !== imageUrl) {
                setDisplayUrl(stableImageKey ? displayUrl : null)
                setLoaded(false)
                setRetryCount(0)
                setRefreshedUrl(nextUrl)
                return
            }

            retryImageMedia()
        }, [
            displayUrl,
            imageCacheKey,
            imageUrl,
            item,
            refreshedUrl,
            retryImageMedia,
            stableImageKey,
        ])

        return (
            <div
                className={cn(
                    styles.payloadImageFrame,
                    loaded && styles.payloadImageFrameLoaded,
                    failed && styles.payloadImageFrameFailed,
                )}
                style={frameStyle}
                data-image-frame-hug={huggedFrameWidth ? '' : undefined}
            >
                {!loaded && !failed && <div className={styles.payloadMediaPlaceholder} />}
                {failed ? (
                    <div className={styles.payloadFallback}>
                        <Image size={22} aria-hidden="true" />
                    </div>
                ) : (
                    <img
                        ref={imageRef}
                        className={styles.payloadImage}
                        style={imageStyle}
                        src={visibleImageUrl || undefined}
                        alt={item.label || 'AI output'}
                        width={width || undefined}
                        height={height || undefined}
                        // WebKit can stall lazy images mounted inside a hidden incoming panel.
                        loading={isIOS ? 'eager' : 'lazy'}
                        decoding="async"
                        onLoad={markLoaded}
                        onError={handleImageError}
                    />
                )}
            </div>
        )
    },
    (prevProps, nextProps) =>
        payloadItemsEqual(prevProps.item, nextProps.item) &&
        prevProps.onMediaLayoutSettled === nextProps.onMediaLayoutSettled &&
        prevProps.availableTileWidth === nextProps.availableTileWidth &&
        prevProps.frameHeightOverride === nextProps.frameHeightOverride &&
        prevProps.mediaFit === nextProps.mediaFit &&
        prevProps.hugWidth === nextProps.hugWidth,
)

// Exported for surfaces that dress standalone media as a payload-style card
// (e.g. the ordinal audio card) and want the same top-right actions dropdown.
export function PayloadTileMenu({
    actions,
    kind,
    chainIndicator,
    dropUp = false,
    creationMenu = false,
    compactCreationMenu = false,
    ariaLabel,
}: {
    actions: PayloadMenuAction[]
    kind?: PayloadContainerItem['kind']
    chainIndicator?: PayloadChainIndicator | null
    dropUp?: boolean
    creationMenu?: boolean
    compactCreationMenu?: boolean
    ariaLabel?: string
}) {
    const [open, setOpen] = useState(false)
    const [preview, setPreview] = useState<React.ReactNode | null>(null)
    const menuRef = useRef<HTMLDivElement | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const menuListRef = useRef<HTMLDivElement | null>(null)
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)

    const updateMenuPosition = () => {
        const rect = buttonRef.current?.getBoundingClientRect()
        if (!rect) return
        setMenuPosition({
            top: dropUp ? rect.top - 4 : rect.bottom + 4,
            left: rect.right,
        })
    }

    useEffect(() => {
        if (!open) return
        updateMenuPosition()

        const containsMenuTarget = (target: EventTarget | null) => {
            if (!target) return false
            const node = target as Node
            return Boolean(menuRef.current?.contains(node) || menuListRef.current?.contains(node))
        }

        const handlePointerDown = (event: PointerEvent) => {
            if (containsMenuTarget(event.target)) return
            suppressNextClick(event)
            event.preventDefault()
            event.stopPropagation()
            event.stopImmediatePropagation()
            setOpen(false)
        }
        const handleFocusIn = (event: FocusEvent) => {
            if (containsMenuTarget(event.target)) return
            setOpen(false)
        }
        const handleViewportChange = () => updateMenuPosition()

        document.addEventListener('pointerdown', handlePointerDown, true)
        document.addEventListener('focusin', handleFocusIn, true)
        window.addEventListener('resize', handleViewportChange)
        window.addEventListener('scroll', handleViewportChange, true)
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true)
            document.removeEventListener('focusin', handleFocusIn, true)
            window.removeEventListener('resize', handleViewportChange)
            window.removeEventListener('scroll', handleViewportChange, true)
        }
    }, [open])

    const isOrdinalMenu = chainIndicator?.kind === 'ordinal'
    const MenuIcon = isOrdinalMenu
        ? OrdinalsIcon
        : kind === 'image'
        ? Image
        : kind === 'video'
        ? Film
        : kind === 'audio'
        ? AudioLines
        : kind === 'file'
        ? File
        : MoreVertical

    return (
        <div className={styles.payloadTileMenu} ref={menuRef}>
            <button
                ref={buttonRef}
                className={cn(
                    styles.payloadTileMenuButton,
                    isNativeIOSApp() && styles.nativeIOSPayloadTileMenuButton,
                    creationMenu && styles.payloadTileCreationMenuButton,
                    compactCreationMenu && styles.payloadTileCreationMenuButtonCompact,
                    isOrdinalMenu && styles.payloadTileOrdinalMenuButton,
                )}
                type="button"
                aria-label={ariaLabel || `${kind || 'Media'} actions`}
                aria-expanded={open}
                aria-haspopup="menu"
                title={ariaLabel || `${kind || 'Media'} actions`}
                onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    updateMenuPosition()
                    if (!open) actions.forEach(action => action.onMenuOpen?.())
                    setOpen(prev => !prev)
                }}
            >
                <MenuIcon
                    size={18}
                    style={
                        chainIndicator?.kind === 'ordinal'
                            ? { position: 'relative', zIndex: 1 }
                            : undefined
                    }
                />
            </button>
            {open &&
                menuPosition &&
                createPortal(
                    <div
                        ref={menuListRef}
                        className={cn(
                            styles.payloadTileMenuList,
                            dropUp && styles.payloadTileMenuListDropUp,
                            actions.some(action => action.detail) &&
                                styles.payloadTileMenuListWithDetail,
                        )}
                        style={{ top: menuPosition.top, left: menuPosition.left }}
                        role="menu"
                    >
                        {actions.map(action =>
                            action.href ? (
                                <a
                                    className={styles.payloadTileMenuItem}
                                    href={action.href}
                                    download={
                                        action.download
                                            ? action.downloadName || action.download
                                            : undefined
                                    }
                                    target={action.external ? '_blank' : undefined}
                                    rel={action.external ? 'noopener noreferrer' : undefined}
                                    key={action.label}
                                    role="menuitem"
                                    onClick={event => {
                                        event.stopPropagation()
                                        setOpen(false)
                                        if (action.download && action.href) {
                                            if ((window as any).KNOVIGATOR_IS_MOBILE) {
                                                action.onClick?.(event, {
                                                    triggerElement: buttonRef.current,
                                                })
                                                return
                                            }

                                            event.preventDefault()
                                            fetchAndDownloadPayloadMedia(action).catch(error => {
                                                console.error(
                                                    'Failed to download payload media:',
                                                    error,
                                                )
                                                triggerDirectDownload(
                                                    action.href as string,
                                                    action.downloadName || action.label || 'media',
                                                )
                                            })
                                            action.onClick?.(event, {
                                                triggerElement: buttonRef.current,
                                            })
                                            return
                                        }
                                        action.onClick?.(event, {
                                            triggerElement: buttonRef.current,
                                        })
                                    }}
                                >
                                    {action.icon || action.chainIndicator ? (
                                        <span className={styles.payloadTileMenuItemIcon}>
                                            {action.icon || (
                                                <PayloadTileChainIndicator
                                                    indicator={action.chainIndicator}
                                                />
                                            )}
                                        </span>
                                    ) : null}
                                    {action.label}
                                </a>
                            ) : (
                                <button
                                    className={cn(
                                        styles.payloadTileMenuItem,
                                        action.working && styles.payloadTileMenuItemWorking,
                                    )}
                                    key={action.label}
                                    type="button"
                                    disabled={action.disabled}
                                    role="menuitem"
                                    onClick={event => {
                                        event.preventDefault()
                                        event.stopPropagation()
                                        if (action.disabled) return
                                        if (action.preview) {
                                            setOpen(false)
                                            setPreview(action.preview)
                                            return
                                        }
                                        if (!action.keepOpenWhilePending) setOpen(false)
                                        const actionResult = action.onClick?.(event, {
                                            triggerElement: buttonRef.current,
                                        })
                                        if (action.keepOpenWhilePending) {
                                            Promise.resolve(actionResult)
                                                .catch(error => {
                                                    console.error(
                                                        'Payload media action failed:',
                                                        error,
                                                    )
                                                })
                                                .finally(() => setOpen(false))
                                        }
                                    }}
                                >
                                    {action.icon || action.chainIndicator ? (
                                        <span className={styles.payloadTileMenuItemIcon}>
                                            {action.icon || (
                                                <PayloadTileChainIndicator
                                                    indicator={action.chainIndicator}
                                                />
                                            )}
                                        </span>
                                    ) : null}
                                    {action.label}
                                    {action.detail && (
                                        <span className={styles.payloadTileMenuItemDetail}>
                                            {action.detail}
                                        </span>
                                    )}
                                </button>
                            ),
                        )}
                    </div>,
                    document.body,
                )}
            {preview &&
                createPortal(
                    <div
                        className={styles.payloadTagPreviewModalBackdrop}
                        onClick={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            setPreview(null)
                        }}
                    >
                        <div
                            className={styles.payloadTagPreviewModal}
                            onClick={event => {
                                event.preventDefault()
                                event.stopPropagation()
                            }}
                        >
                            <button
                                className={styles.payloadTagPreviewModalClose}
                                type="button"
                                aria-label="Close AI run info"
                                onClick={() => setPreview(null)}
                            >
                                ×
                            </button>
                            {preview}
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    )
}

function PayloadTileChainIndicator({ indicator }: { indicator?: PayloadChainIndicator | null }) {
    if (!indicator) return null

    if (indicator.kind === 'ordinal') {
        return (
            <span className={styles.payloadTileChainIcon} aria-label="Ordinal">
                <OrdinalsIcon size={14} />
            </span>
        )
    }

    const txId = indicator.bsvTx?.tx_id || indicator.fallbackTxId || null

    return (
        <BsvTransactionStatusIcon
            bsvTx={indicator.bsvTx}
            fallbackTxId={txId}
            className={cn(styles.payloadTileChainIcon, styles.payloadTileOnchainIcon)}
            iconClassName={styles.payloadTileChainStatusIcon}
            disableLink
            stateClassNameMap={{
                [BSV_TX_STATES.AWAITING_RESUBMISSION]: styles.payloadTileChainIconDim,
                [BSV_TX_STATES.AWAITING_CONFIRMATION]: styles.payloadTileChainIconDim,
            }}
        />
    )
}

// The strip itself: a snap-scrolling flex row pinned to the contract height, plus X's
// hover-only edge chevrons (fine pointers only — touch swipes the strip). The wrapper, not the
// scroller, carries the rounded clip and hairline ring: an absolutely positioned pseudo-element
// inside a scroll container scrolls away with the content.
function PayloadCarouselFrame({
    height,
    count,
    children,
}: {
    height: number
    count: number
    children: React.ReactNode
}) {
    const scrollerRef = useRef<HTMLDivElement | null>(null)
    const scrollFrameRef = useRef<number | null>(null)
    const [edges, setEdges] = useState({ prev: false, next: false })

    const updateEdges = useCallback(() => {
        const scroller = scrollerRef.current
        if (!scroller) return

        const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth
        const prev = scroller.scrollLeft > 1
        const next = scroller.scrollLeft < maxScrollLeft - 1
        setEdges(current =>
            current.prev === prev && current.next === next ? current : { prev, next },
        )
    }, [])

    useLayoutEffect(() => {
        updateEdges()
    }, [updateEdges, count, height])

    useEffect(() => {
        const scroller = scrollerRef.current
        if (!scroller || typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(() => updateEdges())
        observer.observe(scroller)
        return () => observer.disconnect()
    }, [updateEdges])

    useEffect(
        () => () => {
            if (scrollFrameRef.current != null) {
                window.cancelAnimationFrame(scrollFrameRef.current)
            }
        },
        [],
    )

    const handleScroll = useCallback(() => {
        if (scrollFrameRef.current != null) return

        scrollFrameRef.current = window.requestAnimationFrame(() => {
            scrollFrameRef.current = null
            updateEdges()
        })
    }, [updateEdges])

    const scrollByCell = (direction: -1 | 1) => (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        const scroller = scrollerRef.current
        if (!scroller) return

        const cell = scroller.firstElementChild as HTMLElement | null
        const step = (cell?.offsetWidth || scroller.clientWidth) + PAYLOAD_MEDIA_CAROUSEL_GAP_PX
        scroller.scrollBy({ left: direction * step, behavior: 'smooth' })
    }

    return (
        <div className={styles.payloadCarousel}>
            <div
                ref={scrollerRef}
                className={cn(styles.payloadContainer, styles.payloadContainerCarousel)}
                style={
                    {
                        height: `${height}px`,
                        '--payload-carousel-cell-basis': payloadMediaCarouselCellBasis(count),
                    } as React.CSSProperties
                }
                data-testid="answer-payload-carousel"
                onScroll={handleScroll}
                {...panelSwipeExclusionProps}
            >
                {children}
            </div>
            {edges.prev && (
                <button
                    className={cn(styles.payloadCarouselNav, styles.payloadCarouselNavPrev)}
                    type="button"
                    aria-label="Previous media"
                    onClick={scrollByCell(-1)}
                >
                    <ChevronLeft aria-hidden="true" />
                </button>
            )}
            {edges.next && (
                <button
                    className={cn(styles.payloadCarouselNav, styles.payloadCarouselNavNext)}
                    type="button"
                    aria-label="Next media"
                    onClick={scrollByCell(1)}
                >
                    <ChevronRight aria-hidden="true" />
                </button>
            )}
        </div>
    )
}

// The grid's plain container or the carousel strip around the same tiles. Flipping between them
// remounts the tiles — expected, a new layout re-frames every cell anyway, and loaded images
// resume from the in-memory URL cache without a placeholder flash.
function PayloadTilesContainer({
    carousel,
    carouselHeight,
    count,
    statusOnly,
    singleItem,
    children,
}: {
    carousel: boolean
    carouselHeight: number | null
    count: number
    statusOnly: boolean
    singleItem: boolean
    children: React.ReactNode
}) {
    if (carousel && carouselHeight) {
        return (
            <PayloadCarouselFrame height={carouselHeight} count={count}>
                {children}
            </PayloadCarouselFrame>
        )
    }

    return (
        <div
            className={cn(
                styles.payloadContainer,
                statusOnly && styles.statusContainer,
                singleItem && styles.payloadContainerSingle,
            )}
        >
            {children}
        </div>
    )
}

function MediaContainer({
    items,
    statusOnly = false,
    hideSingleModelLabel = false,
    onMediaLayoutSettled,
    postLens = false,
    embed = false,
    estimatedWidth = null,
    mediaLayout = 'grid',
    mediaLayoutKey = null,
    onMediaLayoutChange,
}: {
    items: PayloadContainerItem[]
    statusOnly?: boolean
    hideSingleModelLabel?: boolean
    onMediaLayoutSettled?: MediaLayoutSettledHandler
    postLens?: boolean
    embed?: boolean
    estimatedWidth?: number | null
    mediaLayout?: PayloadMediaLayoutMode
    mediaLayoutKey?: string | null
    onMediaLayoutChange?: () => void
}) {
    // Subscribed before the early return (hooks) — the override store is the one place a
    // viewer's grid/carousel flip lives, so every mounted copy of the post follows it.
    const mediaLayoutOverride = usePayloadMediaLayoutOverride(
        mediaLayout === 'auto' ? mediaLayoutKey : null,
        onMediaLayoutChange,
    )
    const visibleItems = items.filter(Boolean)
    // Subscribed before the early return (hooks): each tile's playlist row
    // reads Add or Remove from the viewer's playlist as it stands now.
    const playlistMediaKeysInPlaylist = useMediaKeysInPlaylist(
        visibleItems.map(item => payloadMediaDockKey(item)),
    )
    if (!visibleItems.length) return null
    // One image + one audio is a single piece of media: cover art and the track it illustrates.
    // The transport sits on the artwork instead of stacking a player below it, here and in
    // every surface that renders this answer — including when it is embedded in another post.
    // lib/combinedMedia owns the predicate so the height estimator agrees from a projection.
    const combinedPair = combinedMediaEnabled()
        ? combinedMediaPair(visibleItems, item => mediaKindFromUrl(item))
        : null
    // A fused release paints ONE tile, so every layout decision below — grid columns, tile
    // width, section chrome — has to count it as one. Only the artwork half is laid out; the
    // audio half renders inside it.
    const layoutItems = combinedPair ? [combinedPair.image] : visibleItems
    const singleItem = layoutItems.length === 1
    // X-style carousel vs grid (lib/payloadMediaLayout). Only a row that is entirely framed
    // visual media can be a strip; a fused image+audio release is one tile, never a strip.
    const carouselEligible =
        !statusOnly &&
        !combinedPair &&
        layoutItems.length > 1 &&
        layoutItems.every(isCarouselMediaItem)
    const resolvedMediaLayout: PayloadMediaLayout = !carouselEligible
        ? 'grid'
        : mediaLayout === 'auto'
        ? mediaLayoutOverride || defaultPayloadMediaLayout(layoutItems)
        : mediaLayout
    const carousel = resolvedMediaLayout === 'carousel'
    const canToggleMediaLayout =
        carouselEligible && mediaLayout === 'auto' && Boolean(mediaLayoutKey)
    // Width contract (lib/mediaBuckets): the width a media tile renders at, used to clamp
    // bucket frame heights so contain-fit media never letterboxes. Applies on mobile
    // (viewport-derived), inside post-lens reply cards, and inside nested PostEmbed answers.
    // Each derives from a width the estimators also use; null otherwise.
    const mobileMediaContentWidth = mobilePayloadMediaContentWidth()
    const mediaContentWidth = payloadMediaContentWidth({
        postLens,
        embed: embed && !singleItem,
        cardWidth: estimatedWidth,
    })
    const availableTileWidth = !mediaContentWidth
        ? null
        : singleItem
        ? mediaContentWidth
        : payloadMediaGridTileWidth(mediaContentWidth)
    // Strip height is the geometry contract: pinned inline on the scroller here, reserved by
    // estimateAnswerPayloadHeight from this same content width.
    const carouselHeight = carousel ? payloadMediaCarouselHeight(mediaContentWidth) : null
    // Reply-card and embedded-answer grids are square by contract: each frame height matches
    // the computed grid column width. Other surfaces retain the grid budget cap (mirrors
    // estimateAnswerPayloadHeight): when 3-4 framed tiles would stack past the single-media
    // height budget, each frame takes the uniform subdivided row height.
    const framedTileBuckets = mediaBucketsEnabled()
        ? layoutItems.flatMap(item => {
              if (item.render || !item.url) return []
              const kind = mediaKindFromUrl(item)
              if (kind !== 'image' && kind !== 'video') return []
              const width = numericImageDimension(item.width)
              const height = numericImageDimension(item.height)
              return [
                  width && height
                      ? bucketedMediaHeight(width, height, availableTileWidth)
                      : RESERVED_DIMLESS_MEDIA_HEIGHT,
              ]
          })
        : []
    const squareTileHeight =
        (postLens || embed) && layoutItems.length > 1 ? availableTileWidth : null
    const gridFrameHeightOverride =
        payloadMediaGridLayout(framedTileBuckets, PAYLOAD_MEDIA_GRID_GAP_PX, {
            squareTileHeight,
        })?.uniformTileHeight ?? null
    const singleCompletedMediaItem =
        singleItem &&
        !statusOnly &&
        !isStatusOnlyItem(layoutItems[0]) &&
        isCompleteStatus(layoutItems[0].status)
    // Multi-item grids draw a hairline per tile, so the outer answer frame is gone and the
    // 8px section inset would pad a border that no longer exists (status-chip grids keep it).
    const bareGridMediaSection = !statusOnly && layoutItems.length > 1
    const singleActiveStatusMediaItem = !statusOnly && isSingleActiveStatusPayload(layoutItems)
    const modelLabelForItem = (item: PayloadContainerItem) => {
        const hasMedia = !!item.url || !!item.render
        if (!hasMedia) return null
        return displayText(item.modelLabel) || (item.tagPreview ? displayText(item.label) : null)
    }
    const metadataForItem = (item: PayloadContainerItem) =>
        Array.isArray(item.metadata)
            ? item.metadata.map(displayText).filter((value): value is string => Boolean(value))
            : []
    // Every cell's dropdown carries the same flip — the choice is per post, not per file.
    const mediaLayoutToggleAction: PayloadMenuAction | null = canToggleMediaLayout
        ? {
              label: carousel ? 'View as grid' : 'View as carousel',
              icon: carousel ? <LayoutGrid size={13} /> : <GalleryHorizontal size={13} />,
              onClick: event => {
                  event.preventDefault()
                  event.stopPropagation()
                  setPayloadMediaLayoutOverride(mediaLayoutKey, carousel ? 'grid' : 'carousel')
              },
          }
        : null
    // Assembles one payload item's dropdown: chain indicator, utilities, playlist, AI-action
    // preview, then creation actions. Factored out of the tile loop so a fused release can
    // build both halves and merge them.
    const buildPayloadMenuGroups = (item: PayloadContainerItem): PayloadMenuGroups => {
        const hasMedia = !!item.url || !!item.render
        const baseMenuActions = item.menuActions || []
        const { chainActions, utilityActions, creationActions } =
            groupPayloadMenuActions(baseMenuActions)
        const itemMediaKind = mediaKindFromUrl(item)
        const playlistMediaKey = payloadMediaDockKey(item, itemMediaKind)
        const itemInPlaylist =
            !!playlistMediaKey && playlistMediaKeysInPlaylist.has(playlistMediaKey)
        // Cinema leads the utilities: it is the one row about watching the clip rather
        // than filing it, and the dock stage it opens is where the same dropdown lives on.
        const cinemaMenuActions: PayloadMenuAction[] =
            mediaDockCinemaEnabled() &&
            shouldAddPayloadCinemaMenuAction({
                hasMedia,
                playlistMediaKey,
                mediaKind: itemMediaKind,
                actions: baseMenuActions,
            })
                ? [
                      {
                          label: 'Cinema',
                          icon: <TvMinimalPlayIcon size={13} />,
                          onClick: (event, { triggerElement }) => {
                              event.preventDefault()
                              event.stopPropagation()
                              openTrackInCinema(playlistMediaKey, 'payload_tile_menu', {
                                  originEl:
                                      triggerElement?.closest('[data-media-track-origin]') || null,
                                  returnFocusEl: triggerElement,
                              })
                          },
                      },
                  ]
                : []
        const playlistMenuActions: PayloadMenuAction[] = shouldAddPayloadPlaylistMenuAction({
            hasMedia,
            playlistMediaKey,
            mediaKind: itemMediaKind,
            actions: baseMenuActions,
        })
            ? [
                  {
                      label: playlistMenuLabel(itemInPlaylist),
                      icon: itemInPlaylist ? <Check size={13} /> : <ListMusic size={13} />,
                      onClick: event => {
                          event.preventDefault()
                          event.stopPropagation()
                          toggleTrackInPlaylist(playlistMediaKey, 'payload_tile_menu')
                      },
                  },
                  {
                      label: PLAY_IN_DOCK_LABEL,
                      icon: <PictureInPicture2 size={13} />,
                      onClick: event => {
                          event.preventDefault()
                          event.stopPropagation()
                          playTrackInDock(playlistMediaKey, 'payload_tile_menu')
                      },
                  },
              ]
            : []

        return {
            kind: itemMediaKind,
            chain: chainActions.length
                ? chainActions
                : item.chainIndicator
                ? [
                      {
                          label: payloadChainActionLabel(item.chainIndicator.kind),
                          chainIndicator: item.chainIndicator,
                      },
                  ]
                : [],
            utility: [
                ...cinemaMenuActions,
                ...utilityActions,
                ...(mediaLayoutToggleAction ? [mediaLayoutToggleAction] : []),
            ],
            playlist: playlistMenuActions,
            tag: shouldShowPayloadTagMenuAction({
                hasTagPreview: Boolean(item.tagPreview),
                status: item.status,
            })
                ? [
                      {
                          label: item.label || item.tagLabel || 'AI action',
                          icon: <Info size={13} />,
                          preview: item.tagPreview,
                      },
                  ]
                : [],
            creation: creationActions,
        }
    }

    const buildPayloadMenuActions = (item: PayloadContainerItem): PayloadMenuAction[] => {
        const groups = buildPayloadMenuGroups(item)
        return [
            ...groups.chain,
            ...groups.utility,
            ...groups.playlist,
            ...groups.tag,
            ...groups.creation,
        ]
    }

    const hasCreationActions = (item: PayloadContainerItem): boolean =>
        groupPayloadMenuActions(item.menuActions || []).creationActions.length > 0

    const singleModelLabel =
        singleItem && !hideSingleModelLabel ? modelLabelForItem(layoutItems[0]) : null
    const singleMetadata =
        singleItem && !hideSingleModelLabel ? metadataForItem(layoutItems[0]) : []

    return (
        <div
            className={cn(
                styles.mediaSection,
                mobileMediaContentWidth && styles.mediaSectionMobile,
                postLens && styles.mediaSectionPostLens,
                singleCompletedMediaItem && styles.singleCompletedMediaSection,
                singleActiveStatusMediaItem && styles.singleActiveStatusMediaSection,
                bareGridMediaSection && styles.mediaSectionBareGrid,
                carousel && styles.mediaSectionCarousel,
            )}
            data-testid="answer-payload-container"
            data-media-layout={carouselEligible ? resolvedMediaLayout : undefined}
        >
            <PayloadTilesContainer
                carousel={carousel}
                carouselHeight={carouselHeight}
                count={layoutItems.length}
                statusOnly={statusOnly}
                singleItem={singleItem}
            >
                {layoutItems.map(item => {
                    const hasMedia = !!item.url || !!item.render
                    const active = isActivePayloadStatus(item.status)
                    const complete = isCompleteStatus(item.status)
                    const showStatus = !complete
                    const mediaStatusOnly = !hasMedia && showStatus
                    const showCreatorFailurePreview =
                        item.status === 'failed' && Boolean(item.creatorFailurePreview)
                    const statusText = displayText(item.message) || statusLabel(item.status)
                    const failureError = getDisplayAiGenerationError(
                        item.errorMessage || statusText,
                    )
                    const creatorFailureErrorText =
                        failureError.summary || failureError.rawText || statusText
                    const modelLabel = modelLabelForItem(item)
                    const metadata = metadataForItem(item)
                    const itemMediaKind = mediaKindFromUrl(item)
                    const placeControlsInsideAudio =
                        singleItem && hasMedia && itemMediaKind === 'audio'
                    const placeControlsInsideVideo =
                        Boolean(item.url) && !item.render && itemMediaKind === 'video'
                    // Combined media folds the track's actions into the artwork's dropdown, so
                    // the top-right menu stays the one place a viewer reaches for either file.
                    const menuActions = combinedPair
                        ? combineMediaMenuGroups(
                              buildPayloadMenuGroups(combinedPair.image),
                              buildPayloadMenuGroups(combinedPair.audio),
                          )
                        : buildPayloadMenuActions(item)
                    const renderTopControls = () =>
                        menuActions.length ? (
                            <div
                                className={cn(
                                    styles.payloadTileTopControls,
                                    isNativeIOSApp() && styles.nativeIOSPayloadTileTopControls,
                                )}
                            >
                                <PayloadTileMenu
                                    actions={menuActions}
                                    // The tile's item is the artwork, but a combined card is a
                                    // track with a cover on it — the button should say audio.
                                    // A status-only run has no completed media to classify yet;
                                    // keep its trigger generic instead of falling back to file.
                                    kind={
                                        combinedPair
                                            ? 'audio'
                                            : item.kind === 'run'
                                            ? undefined
                                            : itemMediaKind
                                    }
                                    // Ordinal state is stronger than the media-type glyph and
                                    // continues to replace it when the artwork is inscribed.
                                    chainIndicator={item.chainIndicator}
                                    creationMenu={
                                        combinedPair
                                            ? hasCreationActions(combinedPair.image) ||
                                              hasCreationActions(combinedPair.audio)
                                            : hasCreationActions(item)
                                    }
                                />
                            </div>
                        ) : null
                    const tileKey = payloadMediaIdentityKey(item) || item.id
                    return (
                        <div
                            key={tileKey}
                            className={cn(
                                styles.payloadTile,
                                carousel && styles.payloadCarouselCell,
                                (itemMediaKind === 'image' || itemMediaKind === 'video') &&
                                    styles.payloadVisualTile,
                                complete && styles.payloadTileComplete,
                                active && styles.payloadTileActive,
                                item.status === 'failed' && styles.payloadTileFailed,
                                item.onClick && !combinedPair && styles.payloadTileClickable,
                                statusOnly && !showCreatorFailurePreview && styles.statusTile,
                                mediaStatusOnly && !showCreatorFailurePreview && styles.statusTile,
                                singleItem && styles.payloadTileSingle,
                                singleActiveStatusMediaItem && styles.payloadTileSingleActiveStatus,
                            )}
                            // Combined media hands image activation to the artwork itself, so
                            // using the transport or its other chrome cannot open the lightbox.
                            onClick={combinedPair ? undefined : item.onClick}
                        >
                            {item.controls}
                            {(item.isMinting || item.mintingFailed) && (
                                <div
                                    className={cn(
                                        styles.payloadTileMintingBadge,
                                        item.mintingFailed && styles.payloadTileMintingBadgeFailed,
                                    )}
                                    aria-live="polite"
                                >
                                    {item.isMinting && (
                                        <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                                    )}
                                    <span>{item.mintingLabel || 'Minting'}</span>
                                </div>
                            )}
                            {item.isMinting && (
                                <div className={styles.payloadTileMintingRail} aria-hidden="true" />
                            )}
                            {item.mintedRecently && (
                                <div
                                    className={cn(
                                        styles.payloadTileMintingRail,
                                        styles.payloadTileMintingRailDone,
                                    )}
                                    aria-hidden="true"
                                />
                            )}
                            {!placeControlsInsideAudio &&
                                !placeControlsInsideVideo &&
                                !combinedPair &&
                                renderTopControls()}
                            <div
                                className={cn(
                                    styles.payloadBody,
                                    (statusOnly || mediaStatusOnly) &&
                                        !showCreatorFailurePreview &&
                                        styles.statusBody,
                                    item.status === 'failed' && styles.failedStatusBody,
                                    showCreatorFailurePreview && styles.creatorFailureBody,
                                    hasMedia &&
                                        itemMediaKind === 'audio' &&
                                        styles.payloadAudioBody,
                                )}
                            >
                                {combinedPair ? (
                                    // The dock-owned placeholder stands in for the fused tile,
                                    // not for the transport strip on its scrim — see
                                    // [data-dock-placeholder-host] in audio.module.scss.
                                    <div
                                        className={styles.payloadCombinedMedia}
                                        data-dock-placeholder-host=""
                                    >
                                        {/* Bucket heights are quantized per aspect class, so a
                                            contain-fit image can leave dead bands inside its
                                            frame — and the transport would dock onto one
                                            instead of onto artwork. Fill the frame instead. */}
                                        <div
                                            className={cn(
                                                styles.payloadCombinedMediaArtwork,
                                                item.onClick && styles.payloadTileClickable,
                                            )}
                                            onClick={item.onClick}
                                        >
                                            <PayloadMedia
                                                item={item}
                                                onMediaLayoutSettled={onMediaLayoutSettled}
                                                availableTileWidth={availableTileWidth}
                                                frameHeightOverride={gridFrameHeightOverride}
                                                mediaFit="cover"
                                                // Hugs like a lone image: the card sits on the
                                                // left edge and the space beside it is the post.
                                                hugImageWidth={!!singleCompletedMediaItem}
                                            />
                                        </div>
                                        <div
                                            className={styles.payloadCombinedMediaScrim}
                                            aria-hidden="true"
                                        />
                                        <PayloadAudio item={combinedPair.audio} overlay />
                                        {renderTopControls()}
                                    </div>
                                ) : placeControlsInsideAudio ? (
                                    <div className={styles.payloadAudioSurface}>
                                        <PayloadMedia
                                            item={item}
                                            onMediaLayoutSettled={onMediaLayoutSettled}
                                            availableTileWidth={availableTileWidth}
                                            frameHeightOverride={gridFrameHeightOverride}
                                        />
                                        {renderTopControls()}
                                    </div>
                                ) : (
                                    hasMedia && (
                                        <PayloadMedia
                                            item={item}
                                            onMediaLayoutSettled={onMediaLayoutSettled}
                                            availableTileWidth={
                                                carousel ? null : availableTileWidth
                                            }
                                            frameHeightOverride={
                                                carousel ? carouselHeight : gridFrameHeightOverride
                                            }
                                            // Cells cover-crop like X's; the grid and the
                                            // lightbox keep the whole image.
                                            mediaFit={carousel ? 'cover' : null}
                                            hugImageWidth={!!singleCompletedMediaItem && !carousel}
                                            topRightControls={
                                                placeControlsInsideVideo
                                                    ? renderTopControls()
                                                    : null
                                            }
                                        />
                                    )
                                )}
                                {showCreatorFailurePreview ? (
                                    <div
                                        className={styles.creatorFailurePanel}
                                        onClick={event => {
                                            event.preventDefault()
                                            event.stopPropagation()
                                        }}
                                    >
                                        <div className={styles.creatorFailurePreviewCard}>
                                            {React.isValidElement(item.creatorFailurePreview)
                                                ? React.cloneElement(
                                                      item.creatorFailurePreview as React.ReactElement<{
                                                          generationErrorMessage?: string | null
                                                          generationErrorRetryButton?: React.ReactNode
                                                      }>,
                                                      {
                                                          generationErrorMessage:
                                                              creatorFailureErrorText,
                                                          generationErrorRetryButton:
                                                              item.onRetry ? (
                                                                  <button
                                                                      className={
                                                                          styles.creatorFailureRetryButton
                                                                      }
                                                                      type="button"
                                                                      onClick={item.onRetry}
                                                                      disabled={item.isRetrying}
                                                                      aria-label="Retry AI generation"
                                                                  >
                                                                      <RefreshCw
                                                                          size={14}
                                                                          strokeWidth={2.5}
                                                                          className={cn(
                                                                              item.isRetrying &&
                                                                                  styles.creatorFailureRetryIconSpinning,
                                                                          )}
                                                                      />
                                                                      <span>
                                                                          {item.isRetrying
                                                                              ? 'Retrying...'
                                                                              : 'Retry'}
                                                                      </span>
                                                                  </button>
                                                              ) : null,
                                                      },
                                                  )
                                                : item.creatorFailurePreview}
                                        </div>
                                    </div>
                                ) : showStatus && item.status === 'failed' ? (
                                    <div
                                        className={cn(
                                            styles.statusOverlay,
                                            styles.failedStatusOverlay,
                                            !hasMedia && styles.statusOnly,
                                        )}
                                    >
                                        <ImageGenerationFailureBubble
                                            errorMessage={item.errorMessage || statusText}
                                            onRetry={item.onRetry}
                                            isRetrying={item.isRetrying}
                                            retryDisabled={!item.onRetry}
                                            showRetry
                                            centered
                                        />
                                    </div>
                                ) : (
                                    showStatus && (
                                        <div
                                            className={cn(
                                                styles.statusOverlay,
                                                !hasMedia && styles.statusOnly,
                                            )}
                                        >
                                            {active && (
                                                <i className="fa fa-circle-o-notch fa-spin" />
                                            )}
                                            <span>{statusText}</span>
                                        </div>
                                    )
                                )}
                            </div>
                            {(modelLabel || metadata.length > 0) && !singleItem && (
                                <div
                                    className={cn(
                                        styles.payloadModelLabel,
                                        carousel && styles.payloadCarouselLabel,
                                    )}
                                >
                                    {modelLabel && (
                                        <span className={styles.payloadModelName}>
                                            {modelLabel}
                                        </span>
                                    )}
                                    {metadata.map(value => (
                                        <span className={styles.payloadMetadataChip} key={value}>
                                            {value}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })}
            </PayloadTilesContainer>
            {(singleModelLabel || singleMetadata.length > 0) && (
                <div className={cn(styles.payloadModelLabel, styles.singlePayloadModelLabel)}>
                    {singleModelLabel && (
                        <span className={styles.payloadModelName}>{singleModelLabel}</span>
                    )}
                    {singleMetadata.map(value => (
                        <span className={styles.payloadMetadataChip} key={value}>
                            {value}
                        </span>
                    ))}
                </div>
            )}
        </div>
    )
}

export function ImageContainer({
    items,
    onMediaLayoutSettled,
}: {
    items: PayloadContainerItem[]
    onMediaLayoutSettled?: MediaLayoutSettledHandler
}) {
    return <MediaContainer items={items} onMediaLayoutSettled={onMediaLayoutSettled} />
}

export function VideoContainer({
    items,
    onMediaLayoutSettled,
}: {
    items: PayloadContainerItem[]
    onMediaLayoutSettled?: MediaLayoutSettledHandler
}) {
    return <MediaContainer items={items} onMediaLayoutSettled={onMediaLayoutSettled} />
}

export function VisualContainer({
    items,
    onMediaLayoutSettled,
}: {
    items: PayloadContainerItem[]
    onMediaLayoutSettled?: MediaLayoutSettledHandler
}) {
    return <MediaContainer items={items} onMediaLayoutSettled={onMediaLayoutSettled} />
}

export function AudioContainer({ items }: { items: PayloadContainerItem[] }) {
    return <MediaContainer items={items} />
}

export function FileContainer({ items }: { items: PayloadContainerItem[] }) {
    return <MediaContainer items={items} />
}

export function StatusContainer({ items }: { items: PayloadContainerItem[] }) {
    return <MediaContainer items={items} statusOnly />
}

// Images and videos share one 'visual' grid row: both render the same fixed-height framed
// tiles, and splitting them into stacked full-width rows let an image+video payload claim two
// media units — the stream's 525px payload cap then sliced the second row mid-frame. As one
// grid they take the same 2-up layout (and single-media height budget) a two-image payload
// gets. Audio, files, and pending-run status chips keep their own rows: their surfaces are
// players/chips, not framed media tiles. Estimator mirror: estimateAnswerPayloadHeight groups
// visual tiles into one grid row the same way.
const PAYLOAD_ROW_KINDS = ['visual', 'audio', 'file', 'status'] as const

export function payloadRowKind(item: PayloadContainerItem): (typeof PAYLOAD_ROW_KINDS)[number] {
    if (isStatusOnlyItem(item)) return 'status'

    const kind = payloadMediaRowKind(mediaKindFromUrl(item))
    if (kind === 'visual') return 'visual'
    if (kind === 'audio') return 'audio'
    if (kind === 'file') return 'file'
    return 'status'
}

export default function PayloadContainer({
    items,
    hideSingleModelLabel = false,
    onMediaLayoutSettled,
    postLens = false,
    embed = false,
    estimatedWidth = null,
    mediaLayout = 'grid',
    mediaLayoutKey = null,
    onMediaLayoutChange,
}: {
    items: PayloadContainerItem[]
    hideSingleModelLabel?: boolean
    onMediaLayoutSettled?: MediaLayoutSettledHandler
    postLens?: boolean
    embed?: boolean
    estimatedWidth?: number | null
    // See PayloadMediaLayoutMode. The key is the answer id; pass none where an override could
    // not be mirrored by the estimator (nested embeds), which pins those to the default.
    mediaLayout?: PayloadMediaLayoutMode
    mediaLayoutKey?: string | null
    // Fires after each mounted copy commits an override, so it can accept its new height.
    onMediaLayoutChange?: () => void
}) {
    const visibleItems = items.filter(Boolean).slice(0, MAX_VISIBLE_PAYLOAD_ITEMS)
    if (!visibleItems.length) return null

    // Keep a combined image+audio pair together. Splitting by visual/audio first would make
    // both MediaContainer calls see a single item, so the pairing predicate could never fire.
    const combinedRow = combinedMediaRow(visibleItems, item => mediaKindFromUrl(item))
    const rows = combinedRow
        ? [combinedRow]
        : PAYLOAD_ROW_KINDS.map(kind => ({
              kind,
              items: visibleItems.filter(item => payloadRowKind(item) === kind),
          })).filter(row => row.items.length > 0)
    const hasFileRow = rows.some(row => row.kind === 'file')
    const hasAudioRow = rows.some(row => row.kind === 'audio')

    if (rows.length === 1) {
        return (
            <MediaContainer
                items={visibleItems}
                hideSingleModelLabel={hideSingleModelLabel}
                onMediaLayoutSettled={onMediaLayoutSettled}
                postLens={postLens}
                embed={embed}
                estimatedWidth={estimatedWidth}
                mediaLayout={mediaLayout}
                mediaLayoutKey={mediaLayoutKey}
                onMediaLayoutChange={onMediaLayoutChange}
            />
        )
    }

    return (
        <div
            className={cn(styles.payloadRows, hasFileRow && styles.mixedFilePayloadRows)}
            // Multi-row payloads (visual media alongside audio/file/status rows — image+video
            // alone is one visual row now) escape the single-kind stream cap via
            // .multiMediaTypePayloadContainer, but the estimators reserve payload height
            // capped at maxPayloadHeight (525). These attributes let answer.module.scss re-cap
            // visual-only multi-row stacks on stream/inactive surfaces while keeping interactive
            // audio rows at their full height. Attributes (matching the data-virtual-static-
            // geometry convention) can't collide with global class names.
            data-static-geometry-mixed-cap={mediaBucketsEnabled() ? 'true' : undefined}
            data-static-geometry-has-audio={hasAudioRow ? 'true' : undefined}
        >
            {rows.map(row => (
                <MediaContainer
                    key={row.kind}
                    items={row.items}
                    statusOnly={row.kind === 'status'}
                    hideSingleModelLabel={false}
                    onMediaLayoutSettled={onMediaLayoutSettled}
                    postLens={postLens}
                    embed={embed}
                    estimatedWidth={estimatedWidth}
                    mediaLayout={mediaLayout}
                    mediaLayoutKey={mediaLayoutKey}
                    onMediaLayoutChange={onMediaLayoutChange}
                />
            ))}
        </div>
    )
}
